import React, { useEffect, useState } from "react";
import { FaEnvelope, FaPhone, FaUser } from "react-icons/fa6";
import type { FormikProps } from "formik";

// components
import InputText from "../form-inputs/input-text.component";
import CustomerProfile from "../user-profile-sidemodal/customer-profile.component";

// global functions and hooks
import { isEmptyObject } from "../../global/function";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { useCustomerSearch } from "../../hooks/useCustomerSearch";

// queries
import type { Customer } from "../../queries/customer.queries";
import type { InvoiceFormValues } from "./invoice-form.types";

interface DataListProps {
	array: Customer[];
	onSelect: (customer: Customer) => void;
}

const DataList = ({ array, onSelect }: DataListProps) => {
	return (
		<div className="datalist-container">
			{array.map((data, i) => (
				<div
					key={i}
					className="list-item"
					onClick={() => onSelect(data)}
				>
					<h5>{data.customer_name}</h5>
					<div className="phone-text">
						<FaPhone />
						<p>{data.customer_mobile}</p>
					</div>
				</div>
			))}
		</div>
	);
};

interface CustomerSelectorProps {
	formik: FormikProps<InvoiceFormValues>;
	formType: "individual" | "bulk";
	// The general single-field setter (unsaved-changes-diff-aware), owned by
	// the parent since it's also used for non-customer fields (sales_date,
	// payment_mode).
	onFieldChange: (fieldName: string, fieldValue: unknown) => void;
	// For the compound updates (formik.setValues with several fields at once)
	// that don't fit the single-field onFieldChange path.
	onUnsavedChanges: (values: Partial<InvoiceFormValues>) => void;
	// Selecting a customer merges their customer_tag into bill_for — the
	// parent owns the `tags` UI state that's derived from bill_for, so this
	// hands it the resulting string rather than duplicating that state here.
	onBillForChange: (billForTag: string) => void;
	isCustomerViewOpen: boolean;
	onCustomerViewChange: (open: boolean) => void;
}

// Buyer identity: name/mobile/PAN inputs + autocomplete, business-customer
// toggle, the selected/new-customer info card, and the "add/edit customer"
// modal. Extracted out of invoice-form.component.tsx (previously ~300 lines
// inline) — the single largest, most self-contained subsection of that file.
function CustomerSelector({
	formik,
	formType,
	onFieldChange,
	onUnsavedChanges,
	onBillForChange,
	isCustomerViewOpen,
	onCustomerViewChange,
}: CustomerSelectorProps) {
	const [selectedCustomer, setSelectedCustomer] = useState<Partial<Customer>>(
		{}
	);

	const handleCustomerType = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.checked) {
			onFieldChange("customer_type", "business");
			onFieldChange("buyer_pan", "");
		} else {
			onFieldChange("customer_type", "individual");
		}
	};

	useEffect(() => {
		if (
			formik.values.company_customer_id &&
			!isEmptyObject(formik.values.customer_info)
		) {
			setSelectedCustomer(formik.values.customer_info || {});
		} else {
			setSelectedCustomer({});
		}
	}, [formik.values.company_customer_id, formik.values.customer_info]);

	const {
		filteredClient,
		handleCustomerInputSearch,
		handleCustomerInputBlur,
	} = useCustomerSearch(formik);

	const handleCustomerInputChange = (
		inputName: string,
		inputValue: string
	) => {
		if (inputName === "customer_mobile" || inputName === "buyer_pan") {
			inputValue = inputValue.replace(/\D/g, "");
		}
		onFieldChange(inputName, inputValue);
		handleCustomerInputSearch(inputName, inputValue);
	};
	const ref = useOutsideClick(handleCustomerInputBlur);

	const handleCustomerSelection = (
		customer: Customer,
		options?: { isProfileUpdate?: boolean },
	) => {
		delete customer.createdAt;
		delete customer.updatedAt;
		setSelectedCustomer(customer);

		// A profile-update refresh (editing the already-linked customer, not
		// picking one) must not touch bill_for — re-appending customer_tag on
		// every edit-save would duplicate/resurrect tags the user removed —
		// and isn't itself a bill/draft edit, so it shouldn't flip bulk mode's
		// unsaved-changes state. It still refreshes buyer_name/buyer_pan/
		// customer_info in formik so the (disabled) buyer fields and info card
		// show the edit immediately; invoice-form re-derives the authoritative
		// buyer_pan/buyer_name from customer_info at actual save time.
		if (options?.isProfileUpdate) {
			formik.setValues({
				...formik.values,
				...customer,
				buyer_name: customer?.customer_name,
				buyer_pan: customer?.customer_pan_vat || "",
				customer_info: customer,
			});
			return;
		}

		const billForTag =
			(formik.values.bill_for || "") + (customer?.customer_tag || "");
		onBillForChange(billForTag);
		const updatedValues: InvoiceFormValues = {
			...formik.values,
			...customer,
			buyer_name: customer?.customer_name,
			buyer_pan: customer?.customer_pan_vat || "",
			bill_for: billForTag,
			customer_info: customer,
		};
		formik.setValues(updatedValues);
		if (formType === "bulk") {
			onUnsavedChanges(updatedValues);
		}
		handleCustomerInputBlur();
	};

	const handleCustomerDeselect = () => {
		const customer: Partial<Customer> = {
			company_customer_id: undefined,
			customer_address: "",
			customer_city: "",
			customer_email: "",
			customer_gender: "",
			customer_id: "",
			customer_mobile: "",
			customer_name: "",
			customer_pan_vat: "",
			customer_tag: "",
			customer_type: "individual",
		};
		setSelectedCustomer({});
		const updatedValues: InvoiceFormValues = {
			...formik.values,
			...customer,
			buyer_name: customer?.customer_name,
			buyer_pan: customer?.customer_pan_vat,
			customer_info: null,
		};
		formik.setValues(updatedValues);
		if (formType === "bulk") {
			onUnsavedChanges(updatedValues);
		}
		handleCustomerInputBlur();
	};

	const handleAddCustomer = () => {
		onCustomerViewChange(true);
	};

	function _renderCustomerInfo() {
		let customerInfo: Partial<Customer> | null = null;
		let isNew = false;

		if (!isEmptyObject(selectedCustomer)) {
			customerInfo = { ...selectedCustomer };
		} else {
			const isBuyerNameNotEmpty =
				!!formik.values.buyer_name && formik.values.buyer_name.trim() !== "";
			const isBuyerMobileNotEmpty =
				!!formik.values.customer_mobile &&
				formik.values.customer_mobile.trim() !== "";

			if (isBuyerNameNotEmpty && isBuyerMobileNotEmpty) {
				isNew = true;
				customerInfo = {
					customer_mobile: formik.values.customer_mobile || "",
					customer_name: formik.values.buyer_name || "",
				};
			}
		}
		if (!customerInfo) {
			return null;
		}

		return (
			<div className="customer-container">
				<div className="customer-details-container">
					<div className="customer-img-container">
						<div className="customer-img">
							{customerInfo?.customer_image ? (
								<img
									crossOrigin="anonymous"
									src={customerInfo?.customer_image}
									alt="profile"
								/>
							) : (
								<FaUser size="3em" />
							)}
						</div>
					</div>
					<div className="customer-info-container">
						<h4 className="info-primarytext cap-display">
							{customerInfo?.customer_name}
						</h4>
						<div className="info-subtext">
							<FaPhone />
							&nbsp;
							{customerInfo?.customer_mobile}
						</div>
						{customerInfo?.customer_email && (
							<div className="info-subtext">
								<FaEnvelope />
								&nbsp;
								{customerInfo?.customer_email}
							</div>
						)}
					</div>
				</div>
				{isNew ? (
					<div className="customer-action-container">
						<div
							className="customer-action-btn"
							onClick={handleAddCustomer}
						>
							Create New Customer Profile
						</div>
					</div>
				) : (
					<div className="customer-action-container">
						<div
							className="customer-action-btn deselect"
							onClick={handleCustomerDeselect}
						>
							Deselect
						</div>
						<div
							className="customer-action-btn"
							onClick={handleAddCustomer}
						>
							Open detailed view
						</div>
					</div>
				)}
			</div>
		);
	}

	const _renderCustomerModal = () => {
		let customerInfo: Partial<Customer>;
		if (!isEmptyObject(selectedCustomer)) {
			// existing customer case
			customerInfo = selectedCustomer;
		} else {
			// new customer case
			customerInfo = {
				company_customer_id: "",
				customer_address: "",
				customer_city: "",
				customer_email: "",
				customer_gender: "",
				customer_id: "",
				customer_image: "",
				customer_tag: "",
				customer_mobile: formik.values.customer_mobile || "",
				customer_name: formik.values.buyer_name || "",
				customer_pan_vat: formik.values.buyer_pan || "",
				customer_type: formik.values.customer_type || "",
			};
		}

		return (
			<CustomerProfile
				isCustomerVisible={isCustomerViewOpen}
				onCustomerModalClose={() => onCustomerViewChange(false)}
				customer={customerInfo}
				onSelectCustomer={(customer, options) =>
					handleCustomerSelection(customer as Customer, options)
				}
			/>
		);
	};

	return (
		<>
			<div className="bill-details-container">
				<div className="row-item">
					<div className="label-title">Bill To</div>

					<div className="input-row-wrapper">
						<div ref={ref}>
							<InputText
								name="buyer_name"
								label="Customer Name"
								placeholder="Enter Customer Name"
								onChange={(e) =>
									handleCustomerInputChange("buyer_name", e.target.value)
								}
								onBlur={formik.handleBlur}
								value={
									// While linked, show the live customer record
									// (refreshed on profile-update saves too) rather
									// than the form's own buyer_name, which is only
									// reconciled from customer_info at actual save
									// time — see invoice-form.component.tsx onSubmit.
									!isEmptyObject(selectedCustomer)
										? selectedCustomer.customer_name || ""
										: formik.values.buyer_name || ""
								}
								disabled={!isEmptyObject(selectedCustomer)}
								error={
									formik.submitCount > 0
										? formik.errors.buyer_name
										: undefined
								}
							/>
						</div>
						<InputText
							name="customer_mobile"
							label="Customer Contact"
							placeholder="Enter Customer Number"
							onChange={(e) =>
								handleCustomerInputChange("customer_mobile", e.target.value)
							}
							onBlur={formik.handleBlur}
							value={
								!isEmptyObject(selectedCustomer)
									? selectedCustomer.customer_mobile || ""
									: formik.values.customer_mobile || ""
							}
							disabled={!isEmptyObject(selectedCustomer)}
							error={
								formik.submitCount > 0
									? formik.errors.customer_mobile
									: undefined
							}
						/>
						{Array.isArray(filteredClient) &&
							filteredClient.length > 0 && (
								<DataList
									array={filteredClient}
									onSelect={handleCustomerSelection}
								/>
							)}
					</div>
					<div className="checkbox-wrapper">
						<input
							type="checkbox"
							name="customer_type"
							id="customer_type"
							className="checkbox-container"
							onChange={(e) => handleCustomerType(e)}
							onBlur={formik.handleBlur}
							checked={
								formik.values.customer_type ===
								"business"
							}
							disabled={!isEmptyObject(selectedCustomer)}
						/>
						<label
							htmlFor="customer_type"
							className={`checkbox-label ${
								!isEmptyObject(selectedCustomer) &&
								"disabled"
							}`}
						>
							Business Customer
						</label>
					</div>

					<InputText
						name="buyer_pan"
						label="VAT / PAN"
						placeholder="Enter Customer VAT/PAN #"
						onChange={(e) =>
							handleCustomerInputChange("buyer_pan", e.target.value)
						}
						onBlur={formik.handleBlur}
						value={
							!isEmptyObject(selectedCustomer)
								? selectedCustomer.customer_pan_vat || ""
								: formik.values.buyer_pan || ""
						}
						disabled={
							!isEmptyObject(selectedCustomer) ||
							formik.values.customer_type !== "business"
						}
						error={
							formik.submitCount > 0 ? formik.errors.buyer_pan : undefined
						}
					/>
				</div>
				<div
					className="row-item"
					style={{
						justifyContent: "flex-end",
					}}
				>
					{/* customer info show or add more customer info */}
					{_renderCustomerInfo()}
				</div>
			</div>
			{isCustomerViewOpen && _renderCustomerModal()}
		</>
	);
}

export default CustomerSelector;
